import { Button, Text } from "@src/straps/base";

const FALLBACK_PRODUCT_OFFERING_TERMS_LINK =
  "https://backpacknetworks.com/documents/product-offering-terms-and-conditions.pdf";
export const FALLBACK_PRIVACY_POLICY_LINK =
  "https://backpacknetworks.com/documents/privacy-policy.pdf";
export const FALLBACK_WEBSITE_TERMS_LINK =
  "https://backpacknetworks.com/documents/terms-of-use.pdf";
const FALLBACK_MARKETPLACE_TERMS_LINK =
  "https://backpacknetworks.com/documents/marketplace-terms-and-conditions.pdf";
const MARKETING_SITE_LINK = "https://backpacknetworks.com/";

export type PolicyLinks = {
  readonly privacy?: string | null;
  readonly website?: string | null;
  readonly product?: string | null;
  readonly marketplace?: string | null;
};

function PolicyLink({
  href,
  dataTestId,
  children,
}: {
  readonly href: string;
  readonly dataTestId: string;
  readonly children: React.ReactNode;
}) {
  return (
    <Button
      component="a"
      href={href}
      target="_blank"
      noFrameVariant="hyperlink"
      className="!inline"
      dataTestId={dataTestId}
    >
      {children}
    </Button>
  );
}

export function TermsAndConditions({
  links,
}: {
  readonly links?: PolicyLinks;
}) {
  return (
    <Text variant="sb_t-14-500" as="span">
      {`I acknowledge the party submitting the building information with this electronic submission is the owner, 
                    property manager, or authorized agent of the identified building and desires to access and use Backpack 
                    software services (the “Product Offering”) and the Backpack Marketplace. 
                    The terms of access to the Product Offering and the Backpack Marketplace are subject to: 
                    the `}
      <PolicyLink
        href={links?.privacy ?? FALLBACK_PRIVACY_POLICY_LINK}
        dataTestId="privacy-policy-link-button"
      >
        Backpack Website Privacy Policy
      </PolicyLink>
      {`, the `}
      <PolicyLink
        href={links?.website ?? FALLBACK_WEBSITE_TERMS_LINK}
        dataTestId="website-terms-link-button"
      >
        Backpack Website Terms of Use Policy
      </PolicyLink>
      {`, the `}
      <PolicyLink
        href={links?.product ?? FALLBACK_PRODUCT_OFFERING_TERMS_LINK}
        dataTestId="product-offering-terms-link-button"
      >
        Backpack Product Offering Terms and Conditions
      </PolicyLink>
      {`, and the `}
      <PolicyLink
        href={links?.marketplace ?? FALLBACK_MARKETPLACE_TERMS_LINK}
        dataTestId="marketplace-terms-link-button"
      >
        Backpack Marketplace Terms and Conditions
      </PolicyLink>
      {`, all of which are incorporated herein by reference and can be found on the Backpack website at `}
      <PolicyLink href={MARKETING_SITE_LINK} dataTestId="marketing-link-button">
        backpacknetworks.com
      </PolicyLink>
    </Text>
  );
}

const nominatedAgreement = (nominatorName = "my nominating organization") =>
  `I hereby authorize, with all requisite authority, to grant full access to all building data to ${nominatorName}, including all of their affiliates, subsidiaries, and representatives.`;

export function NominatorDataUseAgreement({
  content,
  nominatorName,
}: {
  readonly content?: string | null;
  readonly nominatorName?: string;
}) {
  return (
    <Text variant="sb_t-14-500" as="span">
      {content ?? nominatedAgreement(nominatorName)}
    </Text>
  );
}
